import { useTheme } from '../context/ThemeContext';
import { useState, useEffect } from 'react';
import { getTransactions, getCategories, getAccounts, updateTransaction, deleteTransaction, getHousehold, getIncome, setTransactionSplits, unlockTransaction } from '../api/client';


const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const catIcon = (category) => {
  const map = {
    rent: "🏠", groceries: "🛒", "eating out": "🍔", gas: "⛽",
    utilities: "💡", internet: "📶", phone: "📱", misc: "📦",
    income: "💰", transfer: "🔄",
  };
  return map[(category || "").toLowerCase()] || "💳";
};

const getInputStyle = (COLORS) => ({
  padding: "10px 12px", background: COLORS.bg, border: `1px solid ${COLORS.border}`,
  borderRadius: 8, color: COLORS.text, fontSize: 13, fontFamily: "inherit", outline: "none",
});

// ─── Category Picker Modal ────────────────────────────────────────────────────
const CategoryPicker = ({ transaction, categories, incomeSources, onSave, onClose, onDelete, onReload }) => {
  const { COLORS } = useTheme();
  const inputStyle = getInputStyle(COLORS);
  const [selected,         setSelected]         = useState(transaction.category_id || "");
  const [selectedIncome,   setSelectedIncome]   = useState(transaction.income_source_id || "");
  const [txType,           setTxType]           = useState(transaction.type || "expense");
  const [permanent,        setPermanent]        = useState(false);
  const [applyToAll,       setApplyToAll]       = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [note,             setNote]             = useState(transaction.note || "");
  const [excludedFromPace, setExcludedFromPace] = useState(!!transaction.excluded_from_pace);
  const [confirmDelete,    setConfirmDelete]    = useState(false);
  const [deleting,         setDeleting]         = useState(false);
  const [unlocking,        setUnlocking]        = useState(false);

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      await unlockTransaction(transaction.id);
      onReload && await onReload();
      onClose();
    } catch (err) {
      console.error(err);
      setUnlocking(false);
    }
  };

  // ── Split state ────────────────────────────────────────────────────────────
  // The full amount we must divide up (stored positive on the transaction).
  const txAmount = Math.abs(parseFloat(transaction.amount) || 0);
  // splitMode on = divide this charge across categories instead of assigning one.
  const [splitMode, setSplitMode] = useState(!!transaction.is_split);
  // Each row is one slice: a category + a dollar amount (kept as strings for the inputs).
  const [splitRows, setSplitRows] = useState(() =>
    (transaction.splits && transaction.splits.length)
      ? transaction.splits.map(s => ({ category_id: s.category_id || "", amount: String(s.amount) }))
      : [{ category_id: transaction.category_id || "", amount: "" }, { category_id: "", amount: "" }]
  );

  // Live math: how much of the charge is still unallocated. Work in cents so
  // floating-point rounding (0.1 + 0.2 !== 0.3) can't block a valid split.
  const splitCents     = splitRows.reduce((s, r) => s + Math.round((parseFloat(r.amount) || 0) * 100), 0);
  const remainderCents = Math.round(txAmount * 100) - splitCents;
  const remainder      = remainderCents / 100;
  const splitValid     = remainderCents === 0 && splitRows.every(r => r.category_id !== "" && parseFloat(r.amount) > 0);

  const updateSplitRow = (i, field, value) =>
    setSplitRows(rows => rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const addSplitRow    = () => setSplitRows(rows => [...rows, { category_id: "", amount: "" }]);
  const removeSplitRow = (i) => setSplitRows(rows => rows.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Splitting an expense: replace the whole-charge category with the slices.
      if (txType === 'expense' && splitMode) {
        await setTransactionSplits(transaction.id, splitRows.map(r => ({
          category_id: r.category_id === "" ? null : parseInt(r.category_id),
          amount:      parseFloat(r.amount),
        })));
        onReload && await onReload();
        onClose();
        return;
      }

      await updateTransaction(transaction.id, {
        category_id:      txType === 'expense' ? (selected === "" ? null : parseInt(selected)) : null,
        income_source_id: txType === 'income'  ? (selectedIncome === "" ? null : parseInt(selectedIncome)) : null,
        type:             txType,
        permanent,
        apply_to_all:     applyToAll,
        merchant:         transaction.merchant_name || transaction.description,
        note:             note || null,
        excluded_from_pace: excludedFromPace,
      });
      // If this txn was split before but split mode is now off, clear the splits.
      if (transaction.is_split) {
        await setTransactionSplits(transaction.id, []);
        onReload && await onReload();
        onClose();
        return;
      }
      onSave(
        transaction.id,
        txType === 'expense' ? (selected === "" ? null : parseInt(selected)) : null,
        txType,
        txType === 'income' ? (selectedIncome === "" ? null : parseInt(selectedIncome)) : null,
        applyToAll,
      );
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width: "100%", background: COLORS.surface, borderRadius: "20px 20px 0 0", padding: "24px 16px 40px", maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ width: 36, height: 4, background: COLORS.border, borderRadius: 99, margin: "0 auto 20px" }} />

        <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>Edit Transaction</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "0 0 20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {transaction.description}
        </p>

        {/* Locked banner — manual categorization is protected from auto-sync; offer to release it */}
        {transaction.locked && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 16, borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
            <span style={{ fontSize: 15 }}>🔒</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: COLORS.text, fontSize: 12, fontWeight: 600 }}>Locked to your choice</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Auto-categorization won't change this transaction</div>
            </div>
            <button onClick={handleUnlock} disabled={unlocking}
              style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 8, border: `1px solid ${COLORS.accent}`, background: "transparent", color: COLORS.accent, cursor: unlocking ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}
            >{unlocking ? "…" : "Unlock"}</button>
          </div>
        )}

        {/* Type */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Transaction Type</div>
          <div style={{ display: "flex", gap: 8 }}>
            {["expense", "income", "transfer"].map(t => (
              <button key={t} onClick={() => setTxType(t)}
                style={{ flex: 1, padding: "9px", borderRadius: 8, border: `1px solid ${txType === t ? (t === 'income' ? COLORS.green : t === 'transfer' ? COLORS.textMuted : COLORS.red) : COLORS.border}`, background: txType === t ? (t === 'income' ? COLORS.greenGlow : t === 'transfer' ? 'rgba(100,116,139,0.15)' : COLORS.redGlow) : "transparent", color: txType === t ? (t === 'income' ? COLORS.green : t === 'transfer' ? COLORS.text : COLORS.red) : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, textTransform: "capitalize" }}
              >{t}</button>
            ))}
          </div>
        </div>

        {/* Income source picker */}
        {txType === 'income' && (
          <>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Income Source</div>
            <button onClick={() => setSelectedIncome("")}
              style={{ width: "100%", padding: "12px 14px", marginBottom: 8, borderRadius: 10, border: `1px solid ${selectedIncome === "" ? COLORS.accent : COLORS.border}`, background: selectedIncome === "" ? COLORS.accentGlow : "transparent", color: selectedIncome === "" ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14, textAlign: "left" }}
            >
              Unassigned
            </button>
            {incomeSources.map(src => (
              <button key={src.id} onClick={() => setSelectedIncome(src.id)}
                style={{ width: "100%", padding: "12px 14px", marginBottom: 6, borderRadius: 10, border: `1px solid ${selectedIncome === src.id ? COLORS.green : COLORS.border}`, background: selectedIncome === src.id ? COLORS.greenGlow : "transparent", color: selectedIncome === src.id ? COLORS.green : COLORS.text, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span>{src.name}</span>
                <span style={{ color: COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>${parseFloat(src.amount || 0).toLocaleString()}/mo</span>
              </button>
            ))}
          </>
        )}

        {/* Expense category picker */}
        {txType === 'expense' && (
          <>
            {/* Split toggle — divide one charge (e.g. a bundled Apple/Amazon order) across categories */}
            <div
              onClick={() => setSplitMode(v => !v)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", marginBottom: 12, borderRadius: 10, border: `1px solid ${splitMode ? COLORS.accent : COLORS.border}`, background: splitMode ? COLORS.accentGlow : "transparent", cursor: "pointer" }}
            >
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${splitMode ? COLORS.accent : COLORS.border}`, background: splitMode ? COLORS.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {splitMode && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
              <div>
                <div style={{ color: splitMode ? COLORS.accent : COLORS.text, fontSize: 13, fontWeight: 600 }}>Split across categories</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Divide this {fmt(txAmount)} charge into multiple categories</div>
              </div>
            </div>

            {!splitMode && (<>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Category</div>
            <button onClick={() => setSelected("")}
              style={{ width: "100%", padding: "12px 14px", marginBottom: 8, borderRadius: 10, border: `1px solid ${selected === "" ? COLORS.accent : COLORS.border}`, background: selected === "" ? COLORS.accentGlow : "transparent", color: selected === "" ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14, textAlign: "left" }}
            >
              Uncategorized
            </button>
            {["fixed", "variable", "misc", "cc_payment"].map(type => {
              const cats = categories.filter(c => c.type === type);
              if (cats.length === 0) return null;
              return (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>{type}</div>
                  {cats.map(cat => (
                    <button key={cat.id} onClick={() => setSelected(cat.id)}
                      style={{ width: "100%", padding: "12px 14px", marginBottom: 6, borderRadius: 10, border: `1px solid ${selected === cat.id ? COLORS.accent : COLORS.border}`, background: selected === cat.id ? COLORS.accentGlow : "transparent", color: selected === cat.id ? COLORS.accent : COLORS.text, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <span>{cat.name}</span>
                      <span style={{ color: COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{fmt(cat.amount)}/mo</span>
                    </button>
                  ))}
                </div>
              );
            })}
            </>)}

            {/* Split editor — one row per category slice */}
            {splitMode && (
              <div>
                {splitRows.map((row, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <select
                      value={row.category_id}
                      onChange={e => updateSplitRow(i, "category_id", e.target.value)}
                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                    >
                      <option value="">Select category…</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={row.amount}
                      onChange={e => updateSplitRow(i, "amount", e.target.value)}
                      placeholder="0.00"
                      style={{ ...inputStyle, width: 90, textAlign: "right", fontFamily: "'DM Mono', monospace" }}
                    />
                    <button
                      onClick={() => removeSplitRow(i)}
                      disabled={splitRows.length <= 1}
                      style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textMuted, cursor: splitRows.length <= 1 ? "not-allowed" : "pointer", fontSize: 16, lineHeight: 1 }}
                    >×</button>
                  </div>
                ))}

                <button
                  onClick={addSplitRow}
                  style={{ width: "100%", padding: "10px", marginTop: 2, marginBottom: 10, borderRadius: 8, border: `1px dashed ${COLORS.border}`, background: "transparent", color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}
                >+ Add split</button>

                {/* Live remainder — Save is disabled until this hits $0.00 */}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: remainderCents === 0 ? COLORS.greenGlow : COLORS.redGlow, border: `1px solid ${remainderCents === 0 ? COLORS.green : COLORS.red}44` }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600 }}>
                    {remainderCents === 0 ? "Fully allocated" : "Remaining to allocate"}
                  </span>
                  <span style={{ color: remainderCents === 0 ? COLORS.green : COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>
                    {fmt(remainder)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Permanent mapping toggle */}
        {txType !== 'transfer' && !splitMode && (
          <div
            onClick={() => setPermanent(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", marginTop: 8, marginBottom: 4, borderRadius: 10, border: `1px solid ${permanent ? COLORS.accent : COLORS.border}`, background: permanent ? COLORS.accentGlow : "transparent", cursor: "pointer" }}
          >
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${permanent ? COLORS.accent : COLORS.border}`, background: permanent ? COLORS.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {permanent && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
            </div>
            <div>
              <div style={{ color: permanent ? COLORS.accent : COLORS.text, fontSize: 13, fontWeight: 600 }}>Always assign this merchant</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Future transactions from "{transaction.merchant_name || transaction.description}" will be auto-assigned</div>
            </div>
          </div>
        )}

        {/* Apply to all existing transactions toggle */}
        {txType !== 'transfer' && !splitMode && (transaction.merchant_name || transaction.description) && (
          <div
            onClick={() => setApplyToAll(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", marginTop: 4, marginBottom: 4, borderRadius: 10, border: `1px solid ${applyToAll ? COLORS.amber : COLORS.border}`, background: applyToAll ? COLORS.amberGlow : "transparent", cursor: "pointer" }}
          >
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${applyToAll ? COLORS.amber : COLORS.border}`, background: applyToAll ? COLORS.amber : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {applyToAll && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
            </div>
            <div>
              <div style={{ color: applyToAll ? COLORS.amber : COLORS.text, fontSize: 13, fontWeight: 600 }}>Apply to all existing transactions</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Updates all posted "{transaction.merchant_name || transaction.description}" transactions</div>
            </div>
          </div>
        )}

        {/* Exclude-from-pace toggle — one-time/windfall spending (e.g. celebrating a
            bonus) stays in its real category for honest history, but is left out of
            the budget pace/health math for the period so it doesn't look like normal
            spending suddenly spiked. */}
        {txType === 'expense' && !splitMode && (
          <div
            onClick={() => setExcludedFromPace(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", marginTop: 4, marginBottom: 4, borderRadius: 10, border: `1px solid ${excludedFromPace ? COLORS.accent : COLORS.border}`, background: excludedFromPace ? COLORS.accentGlow : "transparent", cursor: "pointer" }}
          >
            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${excludedFromPace ? COLORS.accent : COLORS.border}`, background: excludedFromPace ? COLORS.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {excludedFromPace && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
            </div>
            <div>
              <div style={{ color: excludedFromPace ? COLORS.accent : COLORS.text, fontSize: 13, fontWeight: 600 }}>One-time / windfall spending</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Stays in this category for your records, but won't count against your budget pace</div>
            </div>
          </div>
        )}

        {/* Note field */}
        <div style={{ marginTop: 12, marginBottom: 4 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>Note (optional)</div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add a note to this transaction..."
            rows={2}
            style={{ ...inputStyle, resize: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.4 }}
          />
        </div>

        {/* Delete */}
        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)}
            style={{ width: "100%", marginTop: 12, padding: "10px", background: "transparent", border: `1px solid ${COLORS.red}44`, borderRadius: 10, color: COLORS.red, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}
          >🗑 Delete Transaction</button>
        ) : (
          <div style={{ marginTop: 12, background: COLORS.red + '12', border: `1px solid ${COLORS.red}44`, borderRadius: 10, padding: "12px 14px" }}>
            <p style={{ color: COLORS.text, fontSize: 13, margin: "0 0 10px", textAlign: "center" }}>Delete this transaction permanently?</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmDelete(false)}
                style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}
              >Cancel</button>
              <button onClick={async () => {
                setDeleting(true);
                try {
                  await deleteTransaction(transaction.id);
                  onDelete(transaction.id);
                  onClose();
                } catch (e) { console.error(e); }
                finally { setDeleting(false); }
              }} disabled={deleting}
                style={{ flex: 1, padding: "10px", background: COLORS.red, border: "none", borderRadius: 8, color: "#fff", cursor: deleting ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13 }}
              >{deleting ? "Deleting..." : "Yes, Delete"}</button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: "12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 14 }}
          >Cancel</button>
          {(() => {
            const splitBlocked = txType === 'expense' && splitMode && !splitValid;
            const disabled = saving || splitBlocked;
            return (
              <button onClick={handleSave} disabled={disabled}
                style={{ flex: 2, padding: "12px", background: disabled ? COLORS.border : COLORS.accent, border: "none", borderRadius: 10, color: "#fff", cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14, opacity: disabled ? 0.7 : 1 }}
              >{saving ? "Saving..." : splitBlocked ? "Allocate full amount" : "Save"}</button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// ─── Transactions ─────────────────────────────────────────────────────────────
const Transactions = () => {
  const { COLORS } = useTheme();
  const inputStyle = getInputStyle(COLORS);
  const [transactions, setTransactions] = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [accounts,     setAccounts]     = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [household,    setHousehold]    = useState(null);
  const [loading,      setLoading]      = useState(true);

  const [search,          setSearch]          = useState("");
  const [showFilters,     setShowFilters]      = useState(false);
  const [typeFilter,      setTypeFilter]       = useState("all");
  const [accountFilter,   setAccountFilter]    = useState([]);
  const [categoryFilter,  setCategoryFilter]   = useState([]);
  const [amountMin,       setAmountMin]        = useState("");
  const [amountMax,       setAmountMax]        = useState("");
  const [dateFrom,        setDateFrom]         = useState("");
  const [dateTo,          setDateTo]           = useState("");

  // Category picker state
  const [pickerTx, setPickerTx] = useState(null);
  const [showAccountDD, setShowAccountDD] = useState(false);
  const [showCategoryDD, setShowCategoryDD] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [txRes, catRes, accRes, hhRes, incRes] = await Promise.all([
          getTransactions(), getCategories(), getAccounts(), getHousehold(), getIncome()
        ]);
        const txData  = txRes.data;
        const catData = catRes.data;
        const accData = accRes.data;
        const hhData  = hhRes.data;
        const incData = incRes.data;
        setTransactions(Array.isArray(txData)  ? txData  : txData?.transactions  || txData?.data  || []);
        setCategories( Array.isArray(catData)  ? catData : catData?.data || []);
        setAccounts(   Array.isArray(accData)  ? accData : accData?.data || []);
        setHousehold(  Array.isArray(hhData)   ? null    : hhData?.data || hhData || null);
        setIncomeSources(Array.isArray(incData) ? incData : incData?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Look up category/account names client-side from loaded arrays
  const categoryMap = Object.fromEntries(categories.map(c => [c.id, c]));
  const accountMap  = Object.fromEntries(accounts.map(a => [a.id, a]));

  const activeFilterCount = [
    typeFilter !== "all",
    accountFilter.length > 0,
    categoryFilter.length > 0,
    amountMin !== "" || amountMax !== "",
    dateFrom !== "" || dateTo !== "",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setTypeFilter("all");
    setAccountFilter([]);
    setCategoryFilter([]);
    setAmountMin(""); setAmountMax("");
    setDateFrom(""); setDateTo("");
  };

  // Current billing period derived from household settings
  const monthStart = household?.month_start || 26;
  const _today = new Date();
  const _periodStart = _today.getDate() >= monthStart
    ? new Date(_today.getFullYear(), _today.getMonth(), monthStart)
    : new Date(_today.getFullYear(), _today.getMonth() - 1, monthStart);
  const _periodEnd = new Date(_periodStart.getFullYear(), _periodStart.getMonth() + 1, monthStart - 1);
  const periodFromStr = _periodStart.toISOString().split('T')[0];
  const periodToStr   = _periodEnd.toISOString().split('T')[0];
  const periodLabel   = `${_periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${_periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  const isCurrentPeriod = dateFrom === periodFromStr && dateTo === periodToStr;

  const setCurrentPeriod = () => {
    setDateFrom(periodFromStr);
    setDateTo(periodToStr);
  };

  const filtered = transactions.filter(t => {
    if (typeFilter !== "all" && t.type !== typeFilter) return false;
    if (accountFilter.length > 0 && !accountFilter.includes(String(t.account_id))) return false;
    if (categoryFilter.length > 0) {
      if (categoryFilter.includes("uncategorized")) {
        if (t.category_id) return false;
      } else {
        if (!categoryFilter.includes(String(t.category_id))) return false;
      }
    }
    if (amountMin !== "" && parseFloat(t.amount) < parseFloat(amountMin)) return false;
    if (amountMax !== "" && parseFloat(t.amount) > parseFloat(amountMax)) return false;
    if (dateFrom !== "" && t.date < dateFrom) return false;
    if (dateTo !== "" && t.date > dateTo) return false;
    if (search !== "" && !(t.description || "").toLowerCase().includes(search.toLowerCase()) &&
        !(t.merchant_name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Re-fetch the transaction list from the server (used after changes that touch
  // more than one row or need server-computed fields like splits/is_split).
  const reloadTransactions = async () => {
    try {
      const txRes = await getTransactions();
      const txData = txRes.data;
      setTransactions(Array.isArray(txData) ? txData : txData?.transactions || txData?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // After saving, update transaction in local state (or full reload if apply_to_all)
  const handleCategorySaved = async (txId, categoryId, newType, incomeSourceId, appliedToAll) => {
    if (appliedToAll) {
      // Full reload so all affected transactions show updated category
      await reloadTransactions();
    } else {
      setTransactions(prev => prev.map(t =>
        t.id === txId ? { ...t, category_id: categoryId, type: newType, income_source_id: incomeSourceId } : t
      ));
    }
  };

  const uncategorizedCount = transactions.filter(t => t.type === 'expense' && !t.category_id).length;

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: 'center', paddingTop: 80 }}>Loading...</div>
  );

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ padding: "20px 16px 10px" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Transactions</h2>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "4px 0 0" }}>{filtered.length} results</p>
      </div>

      {/* Billing period quick filter */}
      <div style={{ padding: "0 12px 6px", display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={isCurrentPeriod ? clearFilters : setCurrentPeriod}
          style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${isCurrentPeriod ? COLORS.accent : COLORS.border}`, background: isCurrentPeriod ? COLORS.accentGlow : COLORS.surface, color: isCurrentPeriod ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}
        >
          📅 {isCurrentPeriod ? `Current Period: ${periodLabel}` : `Current Period`}
        </button>
        {isCurrentPeriod && (
          <button onClick={clearFilters} style={{ padding: "7px 10px", borderRadius: 20, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>✕</button>
        )}
      </div>

      {/* Uncategorized banner */}
      {uncategorizedCount > 0 && (
        <div
          onClick={() => { setCategoryFilter(["uncategorized"]); setTypeFilter("expense"); }}
          style={{ margin: "0 12px 10px", padding: "12px 16px", background: COLORS.amberGlow || "rgba(245,158,11,0.1)", border: `1px solid ${COLORS.amber}44`, borderRadius: 12, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <div>
            <div style={{ color: COLORS.amber, fontWeight: 700, fontSize: 13 }}>
              {uncategorizedCount} uncategorized expense{uncategorizedCount !== 1 ? 's' : ''}
            </div>
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>Tap to review and categorize</div>
          </div>
          <span style={{ color: COLORS.amber, fontSize: 18 }}>→</span>
        </div>
      )}

      <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Search + Filter */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search transactions..."
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{ padding: "10px 14px", borderRadius: 12, border: `1px solid ${activeFilterCount > 0 ? COLORS.accent : COLORS.border}`, background: activeFilterCount > 0 ? COLORS.accentGlow : COLORS.surface, color: activeFilterCount > 0 ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
          >
            ⚙️ {activeFilterCount > 0 ? activeFilterCount : "Filter"}
          </button>
        </div>

        {/* Type tabs */}
        <div style={{ display: "flex", gap: 6, background: COLORS.surface, padding: 4, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
          {["all", "expense", "income", "transfer"].map(f => (
            <button key={f} onClick={() => setTypeFilter(f)} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, background: typeFilter === f ? COLORS.accent : "transparent", color: typeFilter === f ? "#fff" : COLORS.textMuted, textTransform: "capitalize" }}>{f}</button>
          ))}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 16 }}>

            {/* Account filter */}
            <div style={{ marginBottom: 14, position: "relative" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Account</div>
              <button onClick={() => { setShowAccountDD(v => !v); setShowCategoryDD(false); }} style={{ width: "100%", padding: "9px 12px", borderRadius: 10, border: `1px solid ${accountFilter.length > 0 ? COLORS.accent : COLORS.border}`, background: COLORS.surface, color: accountFilter.length > 0 ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{accountFilter.length === 0 ? "All Accounts" : `${accountFilter.length} selected`}</span>
                <span>{showAccountDD ? "▲" : "▼"}</span>
              </button>
              {showAccountDD && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, zIndex: 100, marginTop: 4, padding: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13, color: COLORS.text }}>
                    <input type="checkbox" checked={accountFilter.length === 0} onChange={() => setAccountFilter([])} />
                    All Accounts
                  </label>
                  {accounts.filter(a => ["depository","credit"].includes(a.type)).map(a => (
                    <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13, color: COLORS.text }}>
                      <input type="checkbox" checked={accountFilter.includes(String(a.id))} onChange={() => setAccountFilter(prev => prev.includes(String(a.id)) ? prev.filter(x => x !== String(a.id)) : [...prev, String(a.id)])} />
                      {a.custom_name || a.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Category filter */}
            <div style={{ marginBottom: 14, position: "relative" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Category</div>
              <button onClick={() => { setShowCategoryDD(v => !v); setShowAccountDD(false); }} style={{ width: "100%", padding: "9px 12px", borderRadius: 10, border: `1px solid ${categoryFilter.length > 0 ? COLORS.accent : COLORS.border}`, background: COLORS.surface, color: categoryFilter.length > 0 ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{categoryFilter.length === 0 ? "All Categories" : `${categoryFilter.length} selected`}</span>
                <span>{showCategoryDD ? "▲" : "▼"}</span>
              </button>
              {showCategoryDD && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, zIndex: 100, marginTop: 4, padding: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: 220, overflowY: "auto" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13, color: COLORS.text }}>
                    <input type="checkbox" checked={categoryFilter.length === 0} onChange={() => setCategoryFilter([])} />
                    All Categories
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13, color: COLORS.amber }}>
                    <input type="checkbox" checked={categoryFilter.includes("uncategorized")} onChange={() => setCategoryFilter(prev => prev.includes("uncategorized") ? prev.filter(x => x !== "uncategorized") : [...prev, "uncategorized"])} />
                    Uncategorized
                  </label>
                  {categories.map(c => (
                    <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13, color: COLORS.text }}>
                      <input type="checkbox" checked={categoryFilter.includes(String(c.id))} onChange={() => setCategoryFilter(prev => prev.includes(String(c.id)) ? prev.filter(x => x !== String(c.id)) : [...prev, String(c.id)])} />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Date range */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Date Range</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={dateFrom} onChange={e => setDateFrom(e.target.value)} type="date" style={{ ...inputStyle, flex: 1 }} />
                <span style={{ color: COLORS.textMuted }}>–</span>
                <input value={dateTo} onChange={e => setDateTo(e.target.value)} type="date" style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>

            {/* Amount range */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>Amount Range</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={amountMin} onChange={e => setAmountMin(e.target.value)} placeholder="Min $" style={{ ...inputStyle, flex: 1 }} />
                <span style={{ color: COLORS.textMuted }}>–</span>
                <input value={amountMax} onChange={e => setAmountMax(e.target.value)} placeholder="Max $" style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button onClick={clearFilters} style={{ width: "100%", padding: "9px", background: "transparent", border: `1px solid ${COLORS.red}44`, borderRadius: 8, color: COLORS.red, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 13 }}>
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Transaction list */}
      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.length === 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "40px 20px", textAlign: "center" }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>No transactions match your filters</p>
          </div>
        )}
        {filtered.map((t) => {
          const cat     = categoryMap[t.category_id];
          const account = accountMap[t.account_id];
          // A refund is a negative-amount expense (money back reduces category spend).
          const isRefund = t.type === 'expense' && parseFloat(t.amount) < 0;
          // Fix date display — parse date string directly to avoid timezone shift
          const [year, month, day] = (t.date || '').split('T')[0].split('-');
          const dateLabel = year
            ? new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—';
          return (
            <div
              key={t.id}
              onClick={() => setPickerTx(t)}
              style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "13px 15px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", transition: "border-color 0.15s" }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: (t.type === "income" || isRefund) ? COLORS.greenGlow : t.type === "transfer" ? "rgba(100,116,139,0.15)" : COLORS.redGlow, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                {isRefund ? "↩️" : catIcon(cat?.name || t.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t.description}
                  </div>
                  {t.excluded_from_pace && (
                    <span title="One-time/windfall — excluded from budget pace" style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: COLORS.accentGlow, color: COLORS.accent, border: `1px solid ${COLORS.accent}33`, flexShrink: 0 }}>excluded</span>
                  )}
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 3 }}>
                  {dateLabel} · {account?.custom_name || account?.name || 'Unknown'}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ color: (t.type === "income" || isRefund) ? COLORS.green : t.type === "transfer" ? COLORS.textMuted : COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 15 }}>
                  {(t.type === "income" || isRefund) ? "+" : t.type === "transfer" ? "" : "-"}{fmt(t.amount)}
                </div>
                {t.is_split && t.splits?.length ? (
                  <div style={{ color: COLORS.accent, fontSize: 10, marginTop: 2, fontWeight: 600, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    ⑂ Split · {t.splits.map(s => s.category_name).filter(Boolean).join(", ") || `${t.splits.length} categories`}
                  </div>
                ) : (
                  <div style={{ color: isRefund ? COLORS.green : cat ? COLORS.accent : t.type === 'expense' ? COLORS.amber : COLORS.textMuted, fontSize: 10, marginTop: 2, fontWeight: cat ? 400 : 600 }}>
                    {isRefund ? (cat ? `Refund · ${cat.name}` : "Refund") : cat ? cat.name : t.type === 'expense' ? "Tap to categorize" : t.type}
                  </div>
                )}
                {t.note && (
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 2, fontStyle: 'italic', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📝 {t.note}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Category picker modal */}
      {pickerTx && (
        <CategoryPicker
          transaction={pickerTx}
          categories={categories}
          incomeSources={incomeSources}
          onSave={handleCategorySaved}
          onReload={reloadTransactions}
          onClose={() => setPickerTx(null)}
          onDelete={(txId) => setTransactions(prev => prev.filter(t => t.id !== txId))}
        />
      )}
    </div>
  );
};

export default Transactions;
